import { axiosInstance as axios } from "../../../service/axiosManager"

const API_URL = process.env.REACT_APP_HOST_API + "/telemetry"

// Helper to structure the data before sending 
export const createTelemetryBatch = (userId, sessionId, events) => {
  return {
    sessionId,
    userId,
    events
  }
}

export const saveTelemetry = async (sessionId, userId, events) => {
  if (!sessionId || !userId || !events || events.length === 0) return null

  try {
    const response = await axios.post(`${API_URL}/events`, {
      sessionId,
      userId,
      events
    })
    return response.data
  } catch (error) {
    // Silent fail/warn is preferred for logging to avoid disrupting user experience
    console.warn("Error saving telemetry events:", error)
    return null
  }
}

export const getTelemetryEventsBySessionAndUser = async (sessionId, userId) => {
  if (!sessionId || !userId) return []

  try {
    const response = await axios.get(`${API_URL}/session/${sessionId}/user/${userId}`)
    return response.data.events || []
  } catch (error) {
    console.error("Error fetching telemetry events:", error)
    return []
  }
}

export const getTelemetryEventsBySession = async (sessionId) => {
  if (!sessionId) return {}

  try {
    const response = await axios.get(`${API_URL}/session/${sessionId}`)
    return response.data.eventsByUser || {}
  } catch (error) {
    console.error("Error fetching telemetry events:", error)
    return {}
  }
}

// ── Reliable delivery, for the workspace's TelemetryContext ─────────────────────────────
// saveTelemetry above hides failures, and the Reasoning Gap page relies on that. The
// workspace needs to know when a batch did not arrive, so it can send it again.

// Longer than the app's 10s default: telemetry runs in the background, and a slow answer
// from a busy server beats a timeout whose batch the server then saves anyway.
const SEND_TIMEOUT_MS = 30000

// A failed POST: why, whether it is worth sending again, and whether the server may have
// saved it anyway, so that sending it again could store it twice. `reason` is 'http' (the
// server or App Runner's gateway answered `status`), 'timeout', 'network' (no answer: the
// request may have arrived and only the answer been lost), 'offline' (the browser knew it was
// offline, so nothing went out) or 'unsent' (the browser refused the request).
export const telemetryFailure = (reason, status = null) => {
  const failure = { ok: false, reason, status }
  if (reason === 'timeout' || reason === 'network' || status === 504) {
    return { ...failure, retryable: true, maybeStored: true }
  }
  if (reason === 'offline' || reason === 'unsent' || status === 408 || status === 429 || status >= 500) {
    return { ...failure, retryable: true, maybeStored: false }
  }
  // Any other 4xx fails the same way every time.
  return { ...failure, retryable: false, maybeStored: false }
}

const noAnswer = () => telemetryFailure(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'network')

// Saves one batch. Resolves to { ok: true } or a telemetryFailure; never rejects.
export const sendTelemetryBatch = (sessionId, userId, events) => {
  if (!sessionId || !userId || !events || events.length === 0) {
    return Promise.resolve({ ok: false, reason: 'unsent', status: null, retryable: false, maybeStored: false })
  }
  return axios.post(`${API_URL}/events`, { sessionId, userId, events }, { timeout: SEND_TIMEOUT_MS })
    .then(() => ({ ok: true }))
    .catch((error) => {
      const status = error?.response?.status
      if (status) return telemetryFailure('http', status)
      if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return telemetryFailure('timeout')
      return noAnswer()
    })
}

// A request sent with fetch's keepalive is finished by the browser after the page is gone
// (an axios request would be cancelled) - the same request ReasoningGapStudent.jsx sends on
// unload. Browsers cap the bodies of all keepalive requests in flight together at 64 KB;
// this stays a little under it.
export const KEEPALIVE_MAX_BYTES = 60 * 1024

// Same answer as sendTelemetryBatch. A browser without fetch, or one refusing the body as
// over its keepalive limit, leaves the batch 'unsent', to wait for a normal send.
export const sendTelemetryKeepalive = (sessionId, userId, events) => {
  try {
    return fetch(`${API_URL}/events`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, userId, events }),
    })
      .then((response) => (response.ok ? { ok: true } : telemetryFailure('http', response.status)))
      .catch(noAnswer)
  } catch (error) {
    return Promise.resolve(telemetryFailure('unsent'))
  }
}

// Bytes of `text` in UTF-8, or slightly more for characters outside the BMP: the keepalive
// limit counts bytes, not characters.
const utf8Length = (text) => {
  let bytes = 0
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : 3
  }
  return bytes
}

// Splits events, in order, into request bodies of at most maxBytes each, stopping once all
// of them together would pass budgetBytes. `rest` is everything not taken, in order: the
// events past the budget, and any event too large for a keepalive body on its own.
export const splitForKeepalive = (
  sessionId,
  userId,
  events,
  { maxBytes = KEEPALIVE_MAX_BYTES, budgetBytes = KEEPALIVE_MAX_BYTES } = {}
) => {
  const overhead = utf8Length(JSON.stringify({ sessionId, userId, events: [] }))
  const chunks = []
  const rest = []
  let current = null
  let budget = budgetBytes
  let full = false
  for (const event of events) {
    const size = utf8Length(JSON.stringify(event))
    if (full || overhead + size > maxBytes) {
      rest.push(event)
    } else if (current && current.bytes + 1 + size <= maxBytes && 1 + size <= budget) {
      // +1 for the comma between events.
      current.events.push(event)
      current.bytes += 1 + size
      budget -= 1 + size
    } else if (overhead + size <= budget) {
      current = { events: [event], bytes: overhead + size }
      chunks.push(current)
      budget -= overhead + size
    } else {
      full = true
      rest.push(event)
    }
  }
  return { chunks, rest }
}

// The batches TelemetryContext has handed over and the server has not confirmed yet.
//
// One request at a time, oldest batch first. A batch that fails waits and is sent again,
// with exponential backoff and jitter so a class's worth of pages does not retry in step
// after a server hiccup, instead of being dropped as saveTelemetry's callers do. While one
// is in flight or waiting, newer events join the last batch not yet sent (up to
// maxBatchEvents) - never one that has failed, which may be one the server can never save
// - and at most maxBatches wait: past that the oldest is dropped, counted in droppedEvents.
//
// A batch enqueued with hasEventIds (a study condition's: every event carries event_id) is
// also sent again after a failure the server may have saved anyway (a timeout, or no answer
// at all): the copy can be removed by event_id. Other batches keep today's rule for those -
// dropped - so nothing they record can appear twice; they are sent again only when the
// server cannot have saved them (offline, 429, 500, 503...).
//
// onSyncFailed({ failedBatches, queuedEvents, status, reason, droppedEvents }) after every
// failed send, and onSyncRecovered({ resentBatches, droppedEvents }) once nothing waits
// any more, report the gaps to the page.
export const createTelemetryOutbox = ({
  send = sendTelemetryBatch,
  sendKeepalive = sendTelemetryKeepalive,
  onSyncFailed = () => {},
  onSyncRecovered = () => {},
  maxBatches = 50,
  maxBatchEvents = 500,
  // A batch the server answers 500 for this many times is taken to be one it can never
  // save (the database rejects a NUL character in a string, say): without a cap it would
  // hold up every later batch for good. Timeouts, network errors and the gateway's 502-504
  // say nothing about the batch, so they do not count.
  maxServerErrors = 10,
  baseDelayMs = 5000,
  maxDelayMs = 60000,
  random = Math.random,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
} = {}) => {
  // { sessionId, userId, events, hasEventIds, sent, serverErrors, order }; `order` puts a
  // batch whose keepalive send failed back in its place.
  let queue = []
  let nextOrder = 0
  let inFlight = null
  // Whether flushKeepalive has sent a copy of the batch in flight: one copy per send is
  // enough, and a second would take keepalive room from events not sent at all.
  let inFlightCopied = false
  const keepaliveInFlight = new Set()
  let keepaliveBytes = 0
  let retryTimer = null
  let failuresInARow = 0
  // Batches saved since a send failed; null while nothing has failed.
  let resentBatches = null
  let droppedEvents = 0
  let closed = false

  const countEvents = (batches) => batches.reduce((count, batch) => count + batch.events.length, 0)
  const queuedEvents = () => countEvents(queue) + countEvents([...keepaliveInFlight])

  // A report that throws must not stop the retry that follows it.
  const report = (callback, detail) => {
    try {
      callback(detail)
    } catch (error) {
      console.warn('Telemetry sync report failed', error)
    }
  }

  const remove = (batch) => {
    const at = queue.indexOf(batch)
    if (at !== -1) queue.splice(at, 1)
  }

  const drop = (batch) => {
    remove(batch)
    droppedEvents += batch.events.length
  }

  const putBack = (batch) => {
    const at = queue.findIndex((other) => other.order > batch.order)
    if (at === -1) queue.push(batch)
    else queue.splice(at, 0, batch)
  }

  const settleIfDrained = () => {
    if (resentBatches === null || queue.length || inFlight || keepaliveInFlight.size) return
    const detail = { resentBatches, droppedEvents }
    resentBatches = null
    report(onSyncRecovered, detail)
  }

  const scheduleRetry = () => {
    if (retryTimer !== null || closed) return
    const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** Math.max(0, failuresInARow - 1))
    // Anywhere from half to one and a half times the delay.
    retryTimer = setTimer(() => {
      retryTimer = null
      pump()
    }, Math.round(delay * (0.5 + random())))
  }

  // A send of `batch` failed: keep it for another try, or drop it if it can never be saved.
  const failed = (batch, result) => {
    if (result.status === 500) batch.serverErrors += 1
    const retry = result.retryable && (batch.hasEventIds || !result.maybeStored) && batch.serverErrors < maxServerErrors
    if (retry) {
      if (!queue.includes(batch)) putBack(batch)
      failuresInARow += 1
    } else {
      drop(batch)
    }
    if (resentBatches === null) resentBatches = 0
    report(onSyncFailed, {
      failedBatches: queue.length,
      queuedEvents: queuedEvents(),
      status: result.status ?? null,
      reason: result.reason ?? null,
      droppedEvents,
    })
    if (retry) scheduleRetry()
    else pump()
  }

  const pump = () => {
    if (closed || inFlight || retryTimer !== null || queue.length === 0) return
    const batch = queue[0]
    inFlight = batch
    inFlightCopied = false
    batch.sent = true
    Promise.resolve()
      .then(() => send(batch.sessionId, batch.userId, batch.events))
      .catch(() => telemetryFailure('network'))
      .then((result) => {
        inFlight = null
        if (closed) return
        if (result?.ok) {
          remove(batch)
          failuresInARow = 0
          if (resentBatches !== null) resentBatches += 1
          settleIfDrained()
          pump()
        } else {
          failed(batch, result || telemetryFailure('network'))
        }
      })
  }

  // sendNow false only queues the batch, for a flushKeepalive called straight after (the page
  // may be going away): a normal send started here would take the batch, and the browser
  // cancels a normal request at unload.
  const enqueue = ({ sessionId, userId, events, hasEventIds = false }, { sendNow = true } = {}) => {
    if (closed || !events || events.length === 0) return
    const last = queue[queue.length - 1]
    if (
      last && !last.sent &&
      last.sessionId === sessionId && last.userId === userId && last.hasEventIds === hasEventIds &&
      last.events.length + events.length <= maxBatchEvents
    ) {
      last.events = last.events.concat(events)
    } else {
      queue.push({ sessionId, userId, events: events.slice(), hasEventIds, sent: false, serverErrors: 0, order: nextOrder++ })
    }
    const waiting = queue.filter((batch) => batch !== inFlight)
    const over = queue.length - maxBatches
    if (over > 0) waiting.slice(0, over).forEach(drop)
    if (sendNow) pump()
  }

  // Now, not after the backoff: the browser is back online.
  const retryNow = () => {
    if (retryTimer !== null) {
      clearTimer(retryTimer)
      retryTimer = null
    }
    pump()
  }

  // One piece of a batch, sent with keepalive. Called synchronously from flushKeepalive: on
  // pagehide the request has to be made before the handler returns. A copy of the batch in
  // flight only takes up keepalive room: that batch's own send says whether it was saved,
  // and its events are already counted as waiting.
  const sendPiece = (piece) => {
    if (!piece.copy) keepaliveInFlight.add(piece)
    keepaliveBytes += piece.bytes
    let request
    try {
      request = Promise.resolve(sendKeepalive(piece.sessionId, piece.userId, piece.events))
    } catch (error) {
      request = Promise.resolve(telemetryFailure('unsent'))
    }
    request
      .catch(() => telemetryFailure('network'))
      .then((result) => {
        keepaliveInFlight.delete(piece)
        keepaliveBytes -= piece.bytes
        if (closed || piece.copy) return
        if (result?.ok) {
          if (resentBatches !== null) resentBatches += 1
          settleIfDrained()
        } else {
          failed(piece, result || telemetryFailure('network'))
        }
      })
  }

  // The page may be going away (pagehide, or its tab hidden - the last moment a page is
  // sure to run on a phone): send what is waiting with keepalive, oldest first, within the
  // browser's limit for keepalive bodies in flight. Then a copy of the batch in flight, when
  // its events carry event ids: the browser cancels its normal send at unload, and if the
  // page lives on the copy is a duplicate that analysis removes by event_id. The copy comes
  // last, with the room left - that send may have reached the server already, the waiting
  // batches have no other way there - and the batch stays its normal send's to settle.
  // What does not fit goes out with the normal sends, for a page that lives on.
  const flushKeepalive = () => {
    if (closed) return
    let budget = KEEPALIVE_MAX_BYTES - keepaliveBytes
    const pieces = []
    const batches = queue.filter((waiting) => waiting !== inFlight)
    if (inFlight && inFlight.hasEventIds && !inFlightCopied) batches.push(inFlight)
    for (const batch of batches) {
      if (budget <= 0) break
      const { chunks, rest } = splitForKeepalive(batch.sessionId, batch.userId, batch.events, { budgetBytes: budget })
      if (chunks.length === 0) continue
      const copy = batch === inFlight
      if (copy) inFlightCopied = true
      else if (rest.length) batch.events = rest
      else remove(batch)
      for (const chunk of chunks) {
        budget -= chunk.bytes
        pieces.push({ ...batch, events: chunk.events, bytes: chunk.bytes, sent: true, copy })
      }
    }
    pieces.forEach(sendPiece)
    // A batch enqueued with sendNow false that did not fit has nothing else to start it.
    pump()
  }

  // The page's provider is going away while the page stays (it unmounted): one last normal
  // send of everything waiting, with no retries after it.
  const dispose = () => {
    if (closed) return
    closed = true
    if (retryTimer !== null) clearTimer(retryTimer)
    retryTimer = null
    queue.filter((batch) => batch !== inFlight).forEach((batch) => {
      Promise.resolve()
        .then(() => send(batch.sessionId, batch.userId, batch.events))
        .catch(() => {})
    })
    queue = []
  }

  return { enqueue, retryNow, flushKeepalive, dispose, queuedEvents }
}